<template>
  <table class="min-w-full">
    <thead class="bg-gray-50">
      <tr class="first-child:pl-3 last-child:pr-3 child:py-2 sticky top-0 text-xs child:border-b child:border-gray-200 font-semibold text-gray-600 z-10">
        <th class="text-left uppercase tracking-wider bg-gray-50 w-2/12">
          {{ $t('ClaimCertifiedCosts.ClaimTable.contractWorks') }}
        </th>

        <th class="text-right uppercase tracking-wider bg-gray-50 pr-3">
          {{ $t('ClaimCertifiedCosts.ClaimTable.budget') }}
        </th>

        <th class="text-right uppercase tracking-wider bg-green-50">
          {{ $t('ClaimCertifiedCosts.ClaimTable.currentClaim') }}
        </th>

        <th class="text-right uppercase tracking-wider bg-green-50">
          {{ $t('ClaimCertifiedCosts.ClaimTable.currentCertified') }}
        </th>

        <th class="text-right uppercase tracking-wider bg-green-50">
          {{ $t('ClaimCertifiedCosts.ClaimTable.variance') }}
        </th>

        <th class="text-right uppercase tracking-wider bg-green-50">
          {{ $t('ClaimCertifiedCosts.ClaimTable.previous') }}
        </th>

        <th class="text-right uppercase tracking-wider bg-green-50 pr-3">
          {{ $t('ClaimCertifiedCosts.ClaimTable.cumulative') }}
        </th>

        <th class="text-right uppercase tracking-wider bg-blue-50">
          <div class="flex items-center justify-end space-x-2">
            <button
              type="button"
              class="btn-icon btn-icon-default"
              :title="showPercentages ? 'Hide Percentages' : 'Show Percentages'"
              @click.stop="showPercentages = !showPercentages"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-4 h-4"
                :class="[{'rotate-180': !showPercentages}]">
  <path fill-rule="evenodd" d="M16.28 11.47a.75.75 0 010 1.06l-7.5 7.5a.75.75 0 01-1.06-1.06L14.69 12 7.72 5.03a.75.75 0 011.06-1.06l7.5 7.5z" clip-rule="evenodd" />
</svg>
            </button>

            <span v-if="showPercentages">{{ $t('ClaimCertifiedCosts.ClaimTable.currentClaimPercent') }}</span>
          </div>
        </th>

        <th
          v-if="showPercentages"
          class="text-right uppercase tracking-wider bg-blue-50"
        >
          {{ $t('ClaimCertifiedCosts.ClaimTable.currentCertifiedPercent') }}
        </th>

        <th
          v-if="showPercentages"
          class="text-right uppercase tracking-wider bg-blue-50"
        >
          {{ $t('ClaimCertifiedCosts.ClaimTable.previousPercent') }}
        </th>

        <th
          v-if="showPercentages"
          class="text-right uppercase tracking-wider bg-blue-50 pr-3"
        >
          {{ $t('ClaimCertifiedCosts.ClaimTable.cumulativePercent') }}
        </th>

        <th class="text-right uppercase tracking-wider bg-gray-50">
          {{ $t('ClaimCertifiedCosts.ClaimTable.costToComplete') }}
        </th>
      </tr>
    </thead>

    <tbody
      v-if="claim"
      class="bg-white divide-y divide-gray-200"
    >
      <TradeRow
        v-for="trade in trades"
        :key="trade.id"
        :trade="trade"
        :claim="claim"
        :show-percentages="showPercentages"
      />

      <!-- Subtotal Row -->
      <tr class="first-child:pl-3 last-child:pr-3 child:py-4 bg-gray-50 border-t-2 border-gray-200">
        <td class="font-semibold text-gray-900">
          Subtotal
        </td>

        <td class="text-right font-semibold text-gray-900 pr-3">
          <div v-if="totalCosts.trades.budget !== 0">
            <span
              v-if="totalCosts.trades.budget - totalCosts.trades.adjustedBudget !== 0"
              class="text-gray-600"
            >Works:</span> {{ australianCurrency(totalCosts.trades.budget) }}
          </div>

          <div v-if="totalCosts.trades.budget - totalCosts.trades.adjustedBudget !== 0">
            <span
              v-if="totalCosts.trades.budget !== 0"
              class="text-gray-600"
            >Adjustments:</span> {{ australianCurrency(-(totalCosts.trades.budget - totalCosts.trades.adjustedBudget)) }}
          </div>

          <div v-if="totalCosts.trades.budget - totalCosts.trades.adjustedBudget !== 0">
            <span class="text-gray-600">Sum:</span> {{ australianCurrency(totalCosts.trades.adjustedBudget) }}
          </div>
        </td>

        <td class="text-right font-semibold text-gray-900">
          {{ australianCurrency(totalCosts.trades.current_claim) }}
        </td>

        <td class="text-right font-semibold text-gray-900">
          {{ australianCurrency(totalCosts.trades.current_certified) }}
        </td>

        <td
          class="text-right font-semibold"
          :class="{ 'text-red-600': totalCosts.trades.variance !== 0, 'text-gray-900': totalCosts.trades.variance === 0 }"
        >
          {{ australianCurrency(totalCosts.trades.variance) }}
        </td>

        <td class="text-right font-semibold text-gray-900">
          {{ australianCurrency(totalCosts.trades.previous) }}
        </td>

        <td class="text-right font-semibold text-gray-900 pr-3">
          {{ australianCurrency(totalCosts.trades.cumulativeCertified) }}
        </td>

        <td class="text-right font-semibold text-gray-900">
          <span v-if="showPercentages">
            {{ isNumber(totalCosts.trades.current_claim / totalCosts.trades.budget) ? australianNumber((totalCosts.trades.current_claim / totalCosts.trades.budget) * 100) : 0 }}%
          </span>
        </td>

        <td
          v-if="showPercentages"
          class="text-right font-semibold text-gray-900"
        >
          {{ isNumber(totalCosts.trades.current_certified / totalCosts.trades.budget) ? australianNumber((totalCosts.trades.current_certified / totalCosts.trades.budget) * 100) : 0 }}%
        </td>

        <td
          v-if="showPercentages"
          class="text-right font-semibold text-gray-900"
        >
          {{ isNumber(totalCosts.trades.previous / totalCosts.trades.budget) ? australianNumber((totalCosts.trades.previous / totalCosts.trades.budget) * 100) : 0 }}%
        </td>

        <td
          v-if="showPercentages"
          class="text-right font-semibold text-gray-900 pr-3"
        >
          {{ isNumber(totalCosts.trades.cumulativeCertified / totalCosts.trades.budget) ? australianNumber((totalCosts.trades.cumulativeCertified / totalCosts.trades.budget) * 100) : 0 }}%
        </td>

        <td class="text-right font-semibold text-gray-900">
          {{ australianCurrency(Math.abs(totalCosts.trades.budget - totalCosts.trades.cumulativeCertified)) }}
        </td>
      </tr>

      <!-- Variation Orders Section -->
      <template
        v-for="(costs, key) in variationOrderCosts"
        :key="key"
      >
        <VariationOrderRow
          :claim="claim"
          :costs="costs"
          :title="key"
          :variation-order-type="VariationOrderType[key]"
          :show-percentages="showPercentages"
        />
      </template>

      <!-- Variation Orders Subtotal -->
      <tr class="first-child:pl-3 last-child:pr-3 child:py-4 bg-gray-50 border-t-2 border-gray-200">
        <td class="font-semibold text-gray-900">
          Subtotal
        </td>

        <td class="text-right font-semibold text-gray-900 pr-3">
          {{ australianCurrency(totalCosts.variationOrders.budget) }}
        </td>

        <td class="text-right font-semibold text-gray-900">
          {{ australianCurrency(totalCosts.variationOrders.current_claim) }}
        </td>

        <td class="text-right font-semibold text-gray-900">
          {{ australianCurrency(totalCosts.variationOrders.current_certified) }}
        </td>

        <td
          class="text-right font-semibold"
          :class="{ 'text-red-600': totalCosts.variationOrders.variance !== 0, 'text-gray-900': totalCosts.variationOrders.variance === 0 }"
        >
          {{ australianCurrency(totalCosts.variationOrders.variance) }}
        </td>

        <td class="text-right font-semibold text-gray-900">
          {{ australianCurrency(totalCosts.variationOrders.previous) }}
        </td>

        <td class="text-right font-semibold text-gray-900 pr-3">
          {{ australianCurrency(totalCosts.variationOrders.cumulativeCertified) }}
        </td>

        <td class="text-right font-semibold text-gray-900">
          <span v-if="showPercentages">
            {{ totalCosts.variationOrders.budget > 0 ? australianNumber((totalCosts.variationOrders.current_claim / totalCosts.variationOrders.budget) * 100) : 0 }}%
          </span>
        </td>

        <td
          v-if="showPercentages"
          class="text-right font-semibold text-gray-900"
        >
          {{ totalCosts.variationOrders.budget > 0 ? australianNumber((totalCosts.variationOrders.current_certified / totalCosts.variationOrders.budget) * 100) : 0 }}%
        </td>

        <td
          v-if="showPercentages"
          class="text-right font-semibold text-gray-900"
        >
          {{ totalCosts.variationOrders.budget > 0 ? australianNumber((totalCosts.variationOrders.previous / totalCosts.variationOrders.budget) * 100) : 0 }}%
        </td>

        <td
          v-if="showPercentages"
          class="text-right font-semibold text-gray-900 pr-3"
        >
          {{ totalCosts.variationOrders.budget > 0 ? australianNumber((totalCosts.variationOrders.cumulativeCertified / totalCosts.variationOrders.budget) * 100) : 0 }}%
        </td>

        <td class="text-right font-semibold text-gray-900">
          {{ australianCurrency(Math.abs(totalCosts.variationOrders.budget - totalCosts.variationOrders.cumulativeCertified)) }}
        </td>
      </tr>

      <!-- Shared Funds Section -->
      <template v-if="hasSharedFunds && claim">
        <SharedFundRow
          :claim="claim"
          :costs="sharedFundCosts"
          :show-percentages="showPercentages"
        />

        <tr class="first-child:pl-3 last-child:pr-3 child:py-4 bg-gray-50 border-t-2 border-gray-200">
          <td class="font-semibold text-gray-900">
            Subtotal
          </td>

          <td class="text-right font-semibold text-gray-900 pr-3">
            {{ australianCurrency(totalCosts.sharedFunds.budget) }}
          </td>

          <td class="text-right font-semibold text-gray-900">
            {{ australianCurrency(totalCosts.sharedFunds.current_claim) }}
          </td>

          <td class="text-right font-semibold text-gray-900">
            {{ australianCurrency(totalCosts.sharedFunds.current_certified) }}
          </td>

          <td
            class="text-right font-semibold"
            :class="{ 'text-red-600': totalCosts.sharedFunds.variance !== 0, 'text-gray-900': totalCosts.sharedFunds.variance === 0 }"
          >
            {{ australianCurrency(totalCosts.sharedFunds.variance) }}
          </td>

          <td class="text-right font-semibold text-gray-900">
            {{ australianCurrency(totalCosts.sharedFunds.previous) }}
          </td>

          <td class="text-right font-semibold text-gray-900 pr-3">
            {{ australianCurrency(totalCosts.sharedFunds.cumulativeCertified) }}
          </td>

          <td class="text-right font-semibold text-gray-900">
            <span v-if="showPercentages">
              {{ totalCosts.sharedFunds.budget > 0 ? australianNumber((totalCosts.sharedFunds.current_claim / totalCosts.sharedFunds.budget) * 100) : 0 }}%
            </span>
          </td>

          <td
            v-if="showPercentages"
            class="text-right font-semibold text-gray-900"
          >
            {{ totalCosts.sharedFunds.budget > 0 ? australianNumber((totalCosts.sharedFunds.current_certified / totalCosts.sharedFunds.budget) * 100) : 0 }}%
          </td>

          <td
            v-if="showPercentages"
            class="text-right font-semibold text-gray-900"
          >
            {{ totalCosts.sharedFunds.budget > 0 ? australianNumber((totalCosts.sharedFunds.previous / totalCosts.sharedFunds.budget) * 100) : 0 }}%
          </td>

          <td
            v-if="showPercentages"
            class="text-right font-semibold text-gray-900 pr-3"
          >
            {{ totalCosts.sharedFunds.budget > 0 ? australianNumber((totalCosts.sharedFunds.cumulativeCertified / totalCosts.sharedFunds.budget) * 100) : 0 }}%
          </td>

          <td class="text-right font-semibold text-gray-900">
            {{ australianCurrency(Math.abs(totalCosts.sharedFunds.budget - totalCosts.sharedFunds.cumulativeCertified)) }}
          </td>
        </tr>
      </template>

      <!-- Grand Total Row -->
      <tr class="first-child:pl-3 last-child:pr-3 child:py-4 bg-blue-50 border-t-2 border-blue-200 sticky bottom-0">
        <td class="font-bold text-blue-900">
          {{ $t('ClaimCertifiedCosts.ClaimTable.grandTotal') }}
        </td>

        <td class="text-right font-bold text-blue-900 pr-3">
          {{ australianCurrency(grandTotal.budget) }}
        </td>

        <td class="text-right font-bold text-blue-900">
          {{ australianCurrency(grandTotal.current_claim) }}
        </td>

        <td class="text-right font-bold text-blue-900">
          {{ australianCurrency(grandTotal.current_certified) }}
        </td>

        <td
          class="text-right font-bold"
          :class="{ 'text-red-600': grandTotal.variance !== 0, 'text-blue-900': grandTotal.variance === 0 }"
        >
          {{ australianCurrency(grandTotal.variance) }}
        </td>

        <td class="text-right font-bold text-blue-900">
          {{ australianCurrency(grandTotal.previous) }}
        </td>

        <td class="text-right font-bold text-blue-900 pr-3">
          {{ australianCurrency(grandTotal.cumulativeCertified) }}
        </td>

        <td class="text-right font-bold text-blue-900">
          <span v-if="showPercentages">
            {{ grandTotal.budget > 0 ? australianNumber((grandTotal.current_claim / grandTotal.budget) * 100) : 0 }}%
          </span>
        </td>

        <td
          v-if="showPercentages"
          class="text-right font-bold text-blue-900"
        >
          {{ grandTotal.budget > 0 ? australianNumber((grandTotal.current_certified / grandTotal.budget) * 100) : 0 }}%
        </td>

        <td
          v-if="showPercentages"
          class="text-right font-bold text-blue-900"
        >
          {{ grandTotal.budget > 0 ? australianNumber((grandTotal.previous / grandTotal.budget) * 100) : 0 }}%
        </td>

        <td
          v-if="showPercentages"
          class="text-right font-bold text-blue-900 pr-3"
        >
          {{ grandTotal.budget > 0 ? australianNumber((grandTotal.cumulativeCertified / grandTotal.budget) * 100) : 0 }}%
        </td>

        <td class="text-right font-bold text-blue-900">
          {{ australianCurrency(Math.abs(grandTotal.budget - grandTotal.cumulativeCertified)) }}
        </td>
      </tr>
    </tbody>
  </table>
</template>

<script setup lang="ts">
import SharedFundRow from '@/components/ClaimCertifiedCosts/SharedFundRow.vue';
import TradeRow from '@/components/ClaimCertifiedCosts/TradeRow.vue';
import VariationOrderRow from '@/components/ClaimCertifiedCosts/VariationOrderRow.vue';
import { useSignedData } from '@/composables/useSignedData';
import { australianCurrency, australianNumber, isNumber } from '@/helpers';
import TradeItem from '@/models/TradeItem';
import { VariationOrderType } from '@/models/VariationOrder';
import VariationOrderCost from '@/models/VariationOrderCost';
import { useClaimsStore } from '@/store/claims';
import { useSharedFundCostsStore } from '@/store/sharedFundCosts';
import { useSharedFundsStore } from '@/store/sharedFunds';
import { useTradeItemCostsStore } from '@/store/tradeItemCosts';
import { useTradeItemsStore } from '@/store/tradeItems';
import { useTradesStore } from '@/store/trades';
import { useVariationOrderCostsStore } from '@/store/variationOrderCosts';
import { useVariationOrdersStore } from '@/store/variationOrders';
import { Collection } from 'pinia-orm';
import { computed, ref } from 'vue';

const props = defineProps<{
  claimId: string;
}>();

const signedParams = useSignedData();
const tradeItemCostsStore = useTradeItemCostsStore();
const tradeItemsStore = useTradeItemsStore();
const claimsStore = useClaimsStore();
const tradesStore = useTradesStore();
const variationOrdersStore = useVariationOrdersStore();
const variationOrderCostsStore = useVariationOrderCostsStore();
const sharedFundCostsStore = useSharedFundCostsStore();
const sharedFundsStore = useSharedFundsStore();
const showPercentages = ref(false);

tradeItemCostsStore.models.save(signedParams.models.tradeItemCosts);
tradeItemsStore.models.save(signedParams.models.tradeItems);
claimsStore.models.save(signedParams.models.claim);
tradesStore.models.save(signedParams.models.trades);
variationOrdersStore.models.save(signedParams.models.variationOrders);

if(signedParams.models.sharedFunds) {
  sharedFundsStore.models.save(signedParams.models.sharedFunds);
}

const claim = computed(() => {
  return claimsStore.models
    .with('trades', (query) => {
      query.with('trade_items', (query) => {
        query.where('has_children', false)
          .where('budget', (budget) => budget > 0);
      });
    })
    .with('trade_item_costs')
    .with('variation_order_costs', (query) => {
      query.with('variation_order');
    })
    .with('shared_fund_costs', (query) => {
      query.with('shared_fund');
    })
    .find(parseInt(props.claimId));
});

// variation_orders weren't loading on claim for some unknown reason, so we are manually fetching them separately
const variationOrders = computed(() => {
  return variationOrdersStore.models.whereHas('costs', (query) => {
    query.where('claim_id', parseInt(props.claimId));
  }).get();
});

const tradeItemIds = computed(() => {
  return (signedParams.models.tradeItems as TradeItem[]).map((tradeItem) => {
    return tradeItem.id;
  });
});

const trades = computed(() => {
  return tradesStore.models
    .with('trade_items', (query) => {
      query.where('has_children', false);
    })
    .whereHas('trade_items', (query) => {
      query.whereIn('id', tradeItemIds.value);
    })
    .with('trade_item_costs', (query) => {
      query.where('claim_id', parseInt(props.claimId));
    }).get();
});

const hasSharedFunds = computed(() => {
  return sharedFundsStore.models.query().get().length > 0;
});

const sharedFundCosts = computed(() => {
  return sharedFundCostsStore.models
    .with('shared_fund', (query) => {
      query.with('project_group', (projectGroupQuery) => projectGroupQuery.with('projects'));
    })
    .where('claim_id', claim.value?.id)
    .get()
    .sort((a, b) => {
      const aCode = a.shared_fund?.code || '';
      const bCode = b.shared_fund?.code || '';

      return aCode.localeCompare(bCode);
    });
});

const attachedSharedFunds = computed(() => {
  return sharedFundsStore.models.whereHas('costs', (query) => {
    query.where('claim_id', parseInt(props.claimId));
  }).with('project_group', (query) => query.with('projects')).get();
});

const totalCosts = computed(() => {
  const totals = {
    trades: {
      budget: 0,
      adjustedBudget: 0,
      current_claim: 0,
      current_certified: 0,
      cumulativeCertified: 0,
      previous: 0,
      variance: 0,
    },

    variationOrders: {
      budget: 0,
      current_claim: 0,
      current_certified: 0,
      cumulativeCertified: 0,
      previous: 0,
      variance: 0,
    },

    sharedFunds: {
      budget: 0,
      current_claim: 0,
      current_certified: 0,
      cumulativeCertified: 0,
      previous: 0,
      variance: 0,
    },
  };

  if(claim.value) {
    trades.value.forEach((trade) => {
      trade.trade_items.forEach((tradeItem) => {
        if(tradeItem.budget > 0) {
          totals.trades.budget += tradeItem.budget;
        }

        totals.trades.adjustedBudget += tradeItem.budget;
        totals.trades.previous += tradeItem.previous(claim.value);
      });
    });

    claim.value.trade_item_costs.forEach((cost) => {
      totals.trades.current_claim += cost.current_claim;
      totals.trades.current_certified += cost.current_certified;
    });

    variationOrders.value?.forEach((variationOrder) => {
      totals.variationOrders.budget += variationOrder.budget;
    });

    claim.value.variation_order_costs?.forEach((variationOrderCost) => {
      totals.variationOrders.current_claim += variationOrderCost.current_claim;
      totals.variationOrders.current_certified += variationOrderCost.current_certified;
    });

    attachedSharedFunds.value.forEach((sharedFund) => {
      totals.sharedFunds.budget += sharedFund.budget;
      totals.sharedFunds.previous += sharedFund.previous(claim.value);
    });

    sharedFundCosts.value.forEach((sharedFundCost) => {
      totals.sharedFunds.current_claim += sharedFundCost.current_claim ?? 0;
      totals.sharedFunds.current_certified += sharedFundCost.current_certified ?? 0;
    });
  }

  totals.trades.cumulativeCertified = totals.trades.current_certified + totals.trades.previous;
  totals.trades.variance = totals.trades.current_claim - totals.trades.current_certified;

  totals.variationOrders.cumulativeCertified = totals.variationOrders.current_certified +
    totals.variationOrders.previous;

  totals.variationOrders.variance = totals.variationOrders.current_claim - totals.variationOrders.current_certified;

  totals.sharedFunds.cumulativeCertified = totals.sharedFunds.current_certified + totals.sharedFunds.previous;
  totals.sharedFunds.variance = totals.sharedFunds.current_claim - totals.sharedFunds.current_certified;

  return totals;
});

const grandTotal = computed(() => {
  return {
    budget: totalCosts.value.trades.budget + totalCosts.value.variationOrders.budget +
      totalCosts.value.sharedFunds.budget,

    current_claim: totalCosts.value.trades.current_claim + totalCosts.value.variationOrders.current_claim +
      totalCosts.value.sharedFunds.current_claim,

    current_certified: totalCosts.value.trades.current_certified + totalCosts.value.variationOrders.current_certified +
      totalCosts.value.sharedFunds.current_certified,

    variance: totalCosts.value.trades.variance + totalCosts.value.variationOrders.variance +
      totalCosts.value.sharedFunds.variance,

    previous: totalCosts.value.trades.previous + totalCosts.value.variationOrders.previous +
      totalCosts.value.sharedFunds.previous,

    cumulativeCertified: totalCosts.value.trades.cumulativeCertified +
      totalCosts.value.variationOrders.cumulativeCertified + totalCosts.value.sharedFunds.cumulativeCertified,
  };
});

const variationOrderCosts = computed(() => {
  const costs: Partial<{ [k in keyof typeof VariationOrderType]: Collection<VariationOrderCost> }> = {};

  for(const key in VariationOrderType) {
    costs[key] = variationOrderCostsStore.models.with('variation_order').where('claim_id', claim.value?.id).whereHas(
      'variation_order',
      (query) => {
        query.where('type', VariationOrderType[key]);
      },
    ).get()
      .sort((a, b) => {
        const aRef = a.variation_order?.client_ref || a.variation_order?.code || '';
        const bRef = b.variation_order?.client_ref || b.variation_order?.code || '';

        return aRef.localeCompare(bRef);
      });
  }

  return costs;
});
</script>

<style scoped></style>
